<template>
  <div class="reporting-section">
    <div class="section-header">
      <i class="material-icons section-icon">thumb_up</i>
      <h2>{{ $t('dash.reporting.section.engagements.title') }}</h2>
    </div>

    <div class="metric-row">
      <Tooltip :text="networkTooltip('dash.reporting.tooltip.interactions', 'dash.reporting.tooltip.engagement_total')" position="top">
        <MetricCard
          :value="effectiveTotalEngagement"
          :previous-value="prev('totalEngagement')"
          :label="$t('dash.reporting.interactions')"
          icon="fa-solid fa-hand-point-up"
        />
      </Tooltip>
      <MetricCard
        :value="effectiveTotalReactions"
        :previous-value="prev('totalReactions')"
        :label="$t('dash.reporting.reaction')"
        icon="thumb_up"
      />
      <MetricCard
        :value="effectiveTotalComments"
        :previous-value="prev('totalComments')"
        :label="$t('dash.reporting.comment')"
        icon="chat_bubble"
      />
      <MetricCard
        v-if="showShares"
        :value="effectiveTotalShares"
        :previous-value="prev('totalShares')"
        :label="$t('dash.reporting.share')"
        icon="share"
      />
      <MetricCard
        v-if="showSaves"
        :value="stats.page.totalSaves || 0"
        :previous-value="prev('totalSaves')"
        :label="$t('dash.reporting.save')"
        icon="bookmark"
      />
      <Tooltip v-if="stats.page.totalClicks != null" :text="clicksTooltip" :active="network === 'facebook'" position="top">
        <MetricCard
          :value="stats.page.totalClicks || 0"
          :previous-value="prev('totalClicks')"
          :label="$t('dash.reporting.clic')"
          icon="touch_app"
        />
      </Tooltip>
    </div>

    <!-- LinkedIn: use per-day labels from the impression series -->
    <div class="graph-wrap" v-if="network === 'linkedin' ? (liEngagementData.length && liEngagementLabels.length) : (engagementData.length && graphLabels.length)">
      <Graph
        :id="`engagement-${graphId}`"
        :data="network === 'linkedin' ? liEngagementData : engagementData"
        :labels="network === 'linkedin' ? liEngagementLabels : graphLabels"
        height="220px"
        width="100%"
        :per-point-order="true"
      />
    </div>

    <!-- Réactions détaillées (Facebook) -->
    <template v-if="reactionBreakdown">
      <div class="subsection-header">
        <i class="material-icons subsection-icon">mood</i>
        <span>Réactions détaillées</span>
      </div>
      <div class="reactions-breakdown">
        <div class="reaction-item" v-for="r in reactionBreakdown" :key="r.key" v-if="r.value > 0">
          <span class="reaction-emoji">{{ r.emoji }}</span>
          <div class="reaction-bar-wrap">
            <div class="reaction-bar" :style="{ width: r.pct + '%', background: r.color }"></div>
          </div>
          <span class="reaction-count">{{ fmtNum(r.value) }}</span>
          <span class="reaction-pct">{{ r.pct }}%</span>
        </div>
      </div>
    </template>

    <template v-if="calendarData">
      <div class="subsection-header">
        <i class="material-icons subsection-icon">schedule</i>
        <span>{{ $t('dash.reporting.optimal_times') }} en fonction des <span class="heatmap-select-wrap"><select v-model="calendarMetric" class="heatmap-select"><option v-for="m in calendarMetrics" :key="m.key" :value="m.key">{{ m.label }}</option></select><i class="material-icons heatmap-select-arrow">arrow_drop_down</i></span></span>
      </div>
      <div class="calendar-wrap">
        <GraphCalendar
          :key="calendarMetric"
          :id="`calendar-${graphId}-${calendarMetric}`"
          :data="calendarData"
          :optimal-times="optimalTimesData"
          :day-labels="weekDayLabels"
          :unit-label="calendarMetricLabel"
          :is-percent="calendarMetric === 'engagement'"
        />
      </div>
    </template>
  </div>
</template>

<script>
import MetricCard from './panels/MetricCard.vue'
import Graph from './Graph.vue'
import GraphCalendar from './GraphCalendar.vue'
import Tooltip from '../tooltip/tooltipInit.vue'
import moment from 'moment'

export default {
  name: 'ReportingEngagement',
  components: { MetricCard, Graph, GraphCalendar, Tooltip },
  props: {
    stats: { type: Object, required: true },
    graphLabels: { type: Array, default: () => [] },
    page: { type: Object, default: null },
  },
  data() {
    return {
      calendarMetric: 'totalReachEngagement',
    }
  },
  computed: {
    graphId() {
      return this.page ? this.page.id : 'engagement'
    },
    network() { return this.page ? (this.page.network || '') : '' },

    // LinkedIn: derive labels from the per-day series (covers full period, not just post days)
    liEngagementLabels() {
      const base = this.stats.page.reactionsGraph || this.stats.page.commentsGraph ||
                   this.stats.page.sharesGraph    || this.stats.page.link_clicks_graph
      if (!Array.isArray(base) || !base.length) return []
      return base.slice().sort(function(a, b) { return a.date - b.date })
        .map(function(d) { return moment(d.date * 1000).format('MM/DD/YYYY') })
    },
    liEngagementData() {
      var toArr = function(arr) {
        if (!Array.isArray(arr) || !arr.length) return []
        return arr.slice().sort(function(a, b) { return a.date - b.date }).map(function(d) { return d.total || 0 })
      }
      var reactions = toArr(this.stats.page.reactionsGraph)
      var comments  = toArr(this.stats.page.commentsGraph)
      var shares    = toArr(this.stats.page.sharesGraph)
      var clicks    = toArr(this.stats.page.link_clicks_graph)
      var bars = [], lines = []
      if (reactions.some(function(v) { return v > 0 }))
        bars.push({ label: this.$t('dash.reporting.reaction'), data: reactions, type: 'bar', color: '#F56565' })
      if (comments.some(function(v) { return v > 0 }))
        bars.push({ label: this.$t('dash.reporting.comment'), data: comments, type: 'bar', color: '#4299E1' })
      if (shares.some(function(v) { return v > 0 }))
        bars.push({ label: this.$t('dash.reporting.share'), data: shares, type: 'bar', color: '#68D391' })
      if (clicks.some(function(v) { return v > 0 }))
        lines.push({ label: this.$t('dash.reporting.clic'), data: clicks, type: 'line', color: '#F59E0B' })
      bars.sort(function(a, b) {
        var sumA = a.data.reduce(function(s, v) { return s + v }, 0)
        var sumB = b.data.reduce(function(s, v) { return s + v }, 0)
        return sumB - sumA
      })
      return bars.concat(lines)
    },
    posts() {
      return this.stats.posts || (this.stats.page && this.stats.page.posts) || []
    },
    postsTotals() {
      var reactions = 0, comments = 0, shares = 0
      this.posts.forEach(function(p) {
        reactions += p.post_reactions_sum || p.like_count || 0
        comments  += p.comments_count || 0
        shares    += p.shares_count || 0
      })
      return { reactions: reactions, comments: comments, shares: shares, engagement: reactions + comments + shares }
    },
    postsDayMap() {
      var map = {}
      this.posts.forEach(function(p) {
        if (!p.created_time) return
        var day = p.created_time.substring(0, 10)
        if (!map[day]) map[day] = { reactions: 0, comments: 0, shares: 0 }
        map[day].reactions += p.post_reactions_sum || p.like_count || 0
        map[day].comments  += p.comments_count || 0
        map[day].shares    += p.shares_count || 0
      })
      return map
    },
    graphDateBuckets() {
      return (this.stats.page.nb_post_graph || [])
        .slice().sort(function(a, b) { return a.date - b.date })
        .map(function(d) { return new Date(d.date * 1000).toISOString().substring(0, 10) })
    },
    effectiveTotalReactions() {
      return this.stats.page.totalReactions || this.postsTotals.reactions
    },
    effectiveTotalComments() {
      return this.stats.page.totalComments || this.postsTotals.comments
    },
    // Instagram : partages/enregistrements disponibles depuis total_interactions (cf. engine reportingFormat.formatReportingIG)
    // Tooltip propre au réseau (clé <base>_<network>), repli sur la clé générique
    networkTooltip() {
      var self = this
      return function(base, fallback) {
        var key = base + '_' + self.network
        return self.$te(key) ? self.$t(key) : self.$t(fallback || base)
      }
    },
    // Facebook : la carte Clics compte les clics sur liens ; le tooltip donne aussi le total tous clics
    clicksTooltip() {
      var total = (this.posts || []).reduce(function(s, p) { return s + (p.total_clicks || p.link_clicks || 0) }, 0)
      return this.$t('dash.reporting.tooltip.clicks_card_facebook', { total: total.toLocaleString() })
    },
    showShares() {
      return this.network !== 'instagram' || this.stats.page.totalShares != null
    },
    showSaves() {
      return this.network === 'instagram' && this.stats.page.totalSaves != null
    },
    effectiveTotalShares() {
      return this.stats.page.totalShares || this.postsTotals.shares
    },
    effectiveTotalEngagement() {
      return this.stats.page.totalEngagement || this.postsTotals.engagement
    },
    engagementData() {
      const bars = []
      const lines = []
      var self = this

      var reactionsArr = (this.stats.page.reactionsGraph || [])
        .sort(function(a, b) { return a.date - b.date }).map(function(d) { return d.total || 0 })
      if (!reactionsArr.length) reactionsArr = self.buildGraphFromPosts('reactions')
      if (reactionsArr.some(function(v) { return v > 0 }))
        bars.push({ label: this.$t('dash.reporting.reaction'), data: reactionsArr, type: 'bar', color: '#F56565' })

      var commentsArr = (this.stats.page.commentsGraph || [])
        .sort(function(a, b) { return a.date - b.date }).map(function(d) { return d.total || 0 })
      if (!commentsArr.length) commentsArr = self.buildGraphFromPosts('comments')
      if (commentsArr.some(function(v) { return v > 0 }))
        bars.push({ label: this.$t('dash.reporting.comment'), data: commentsArr, type: 'bar', color: '#4299E1' })

      if (this.showShares) {
        var sharesArr = (this.stats.page.sharesGraph || [])
          .sort(function(a, b) { return a.date - b.date }).map(function(d) { return d.total || 0 })
        if (!sharesArr.length) sharesArr = self.buildGraphFromPosts('shares')
        if (sharesArr.some(function(v) { return v > 0 }))
          bars.push({ label: this.$t('dash.reporting.share'), data: sharesArr, type: 'bar', color: '#68D391' })
      }

      const clicks = (this.stats.page.link_clicks_graph || [])
        .sort(function(a, b) { return a.date - b.date }).map(function(d) { return d.total || 0 })
      if (clicks.some(function(v) { return v > 0 })) lines.push({ label: this.$t('dash.reporting.clic'), data: clicks, type: 'line', color: '#F59E0B' })

      // Tri DESC par somme totale : la plus grande barre est dessinée en premier (derrière),
      // la plus petite en dernier (devant) → les petites valeurs restent visibles à chaque point
      bars.sort(function(a, b) {
        var sumA = a.data.reduce(function(s, v) { return s + v }, 0)
        var sumB = b.data.reduce(function(s, v) { return s + v }, 0)
        return sumB - sumA
      })

      return bars.concat(lines)
    },
    reactionBreakdown() {
      var network = this.page && this.page.network
      if (network !== 'facebook') return null
      var posts = (this.stats.posts || []).filter(function(p) { return p.post_reactions && p.post_reactions[0] && p.post_reactions[0].value })
      if (!posts.length) return null
      var defs = [
        { key: 'like', emoji: '👍', color: '#4299e1' },
        { key: 'love', emoji: '❤️', color: '#f56565' },
        { key: 'haha', emoji: '😂', color: '#ecc94b' },
        { key: 'wow', emoji: '😮', color: '#ed8936' },
        { key: 'sorry', emoji: '😢', color: '#9f7aea' },
        { key: 'anger', emoji: '😡', color: '#fc8181' },
      ]
      var totals = {}
      defs.forEach(function(d) { totals[d.key] = 0 })
      posts.forEach(function(p) {
        var v = p.post_reactions[0].value
        defs.forEach(function(d) { totals[d.key] += (v[d.key] || 0) })
      })
      var grand = defs.reduce(function(sum, d) { return sum + totals[d.key] }, 0)
      if (!grand) return null
      return defs.map(function(d) {
        return Object.assign({}, d, {
          value: totals[d.key],
          pct: grand > 0 ? Math.round(totals[d.key] / grand * 100) : 0,
        })
      }).sort(function(a, b) { return b.value - a.value })
    },
    weekDayLabels() {
      return [
        this.$t('dash.week.monday'),
        this.$t('dash.week.tuesday'),
        this.$t('dash.week.wednesday'),
        this.$t('dash.week.thursday'),
        this.$t('dash.week.friday'),
        this.$t('dash.week.saturday'),
        this.$t('dash.week.sunday'),
      ]
    },
    calendarMetrics() {
      return [
        { key: 'totalReachEngagement', label: this.$t('dash.reporting.interactions') },
        { key: 'totalImpressions',     label: this.$t('dash.reporting.impression') },
        { key: 'totalClicks',          label: this.$t('dash.reporting.clic') },
        { key: 'engagement',           label: this.$t('dash.reporting.engagement_rate') },
      ]
    },
    calendarMetricLabel() {
      var m = this.calendarMetrics.find(function(x) { return x.key === this.calendarMetric }.bind(this))
      return m ? m.label : ''
    },
    optimalTimesData() {
      return this.stats && this.stats.page ? this.stats.page.optimalTimes : null
    },
    calendarData() {
      var ot = this.optimalTimesData
      if (!ot || !ot.optimalTimeSlots) return null
      var metric = this.calendarMetric
      return Object.values(ot.optimalTimeSlots)
        .map(function(hours) {
          return hours.map(function(h) {
            if (!h) return 0
            if (metric === 'engagement') {
              var imp = h.totalImpressions || 0
              return imp > 0 ? ((h.totalReachEngagement || 0) + (h.totalClicks || 0)) / imp * 100 : 0
            }
            return h[metric] || 0
          })
        })
    },
  },
  methods: {
    buildGraphFromPosts(field) {
      var map = this.postsDayMap
      return this.graphDateBuckets.map(function(day) {
        return (map[day] && map[day][field]) || 0
      })
    },
    prev(field) {
      const p = this.stats.page.previous_period
      return p && p[field] != null ? p[field] : null
    },
    fmtNum(n) {
      if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M'
      if (n >= 1000) return (n / 1000).toFixed(1) + 'k'
      return n.toString()
    },
  },
}
</script>

<style scoped>
.reporting-section {
  background: #fff;
  border-radius: 5.6px;
  padding: 20px 24px;
  box-shadow: 0 2px 5.6px rgba(107,31,77,0.06);
  border: 1px solid #f1edf5;
}
.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
}
.section-icon { color: #6B1F4D; font-size: 22px; }
.section-header h2 { font-size: 16px; font-weight: 700; color: #121826; margin: 0; }
.metric-row {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 20px;
}
/* carte enveloppée dans un Tooltip : même largeur qu'une MetricCard directe */
.metric-row > .hasTooltip {
  flex: 1;
  min-width: 160px;
  display: flex;
}
.graph-wrap {
  width: 100%;
  height: 220px;
}
.subsection-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 24px 0 12px;
  font-size: 13px;
  font-weight: 600;
  color: #555E75;
  flex-wrap: wrap;
}
.subsection-icon { font-size: 18px; color: #6B1F4D; }
.heatmap-select-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}
.heatmap-select {
  appearance: none;
  -webkit-appearance: none;
  border: none;
  border-bottom: 2px solid #6B1F4D;
  background: transparent;
  color: #6B1F4D;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  padding: 0 18px 0 2px;
  outline: none;
}
.heatmap-select-arrow {
  position: absolute;
  right: 0;
  font-size: 18px;
  color: #6B1F4D;
  pointer-events: none;
  line-height: 1;
}
.calendar-wrap {
  width: 100%;
  height: 360px;
  overflow: hidden;
}
.reactions-breakdown {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}
.reaction-item {
  display: flex;
  align-items: center;
  gap: 10px;
}
.reaction-emoji { font-size: 18px; width: 26px; text-align: center; flex-shrink: 0; }
.reaction-bar-wrap {
  flex: 1;
  height: 8px;
  background: #f1edf5;
  border-radius: 4px;
  overflow: hidden;
}
.reaction-bar {
  height: 100%;
  border-radius: 4px;
  transition: width 0.4s ease;
}
.reaction-count { font-size: 13px; font-weight: 600; color: #121826; width: 50px; text-align: right; flex-shrink: 0; }
.reaction-pct { font-size: 12px; color: #9ba3b7; width: 36px; text-align: right; flex-shrink: 0; }
</style>
